import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import qahoy from "views_shared/app/javascript/scripts/qahoy";
import qapi from "views_shared/app/javascript/api";
import { prProfileUrl, companyUrl } from "views_shared/app/javascript/utils/urls";
import ListRow from "../../../profiles/ListRow";
import SectionCard from "../../../profiles/SectionCard";
import SidebarSkeletonList from "./sidebar_skeleton_list";
import { CompanyContext } from "../../data/company_context_management";

function PrProfileList({ previewMode = false }) {
  const { companyId, company, viewerContext, setPrProfilesExist, setOpenEditModal } = useContext(CompanyContext);
  const editable = !!viewerContext?.editable;
  const [isLoading, setIsLoading] = useState(true);
  const [prProfiles, setPrProfiles] = useState([]);
  const [asyncError, setAsyncError] = useState();

  // Retrieve the pr_profiles list from the API
  useEffect(() => {
    let componentIsMounted = true;
    const fetchPrProfiles = async () => {
      try {
        if (companyId) {
          setIsLoading(true);
          qahoy.track("Companies#getPrProfiles");

          const response = await qapi.representations.getAllForPromotedCompany(companyId, { perPage: 9999 });
          if (componentIsMounted && response.data) {
            const prProfileResults = response.data
              .map((item) => ({
                id: item.promoter_profile_id,
                promoter_company_id: item.promoter_company_id,
                promoter_company_name: item.promoter_company_name,
                promoter_company_profile_image_url: item.promoter_company_profile_image_url,
                promoter_company_slug: item.promoter_company_slug,
                promoter_company_migrate_id: item.promoter_company_migrate_id,
                name: item.promoter_profile_name,
                profile_image_url: item.promoter_profile_image_url,
                slug: item.promoter_profile_slug,
                migrate_id: item.promoter_profile_migrate_id,
                title: item.promoter_job_title,
              }))
              .reduce((unique, item) => {
                return unique.find((e) => e.id === item.id) ? unique : [...unique, item];
              }, []);

            if (prProfileResults) {
              if (previewMode) {
                // Limit to 3 records if we're in preview mode
                setPrProfiles(prProfileResults.slice(0, 3));
              } else {
                setPrProfiles(prProfileResults);
              }
            }
          }
          if (componentIsMounted) setIsLoading(false);
        }
      } catch (error) {
        setAsyncError(error);
      }
    };
    fetchPrProfiles();

    // Cleanup function to avoid "Can't perform state update on unmounted component" error
    return () => {
      componentIsMounted = false;
    };
  }, [companyId, previewMode]);

  // Update company context to indicate PR user records exist
  useEffect(() => {
    setPrProfilesExist(prProfiles.length > 0);
  }, [prProfiles, setPrProfilesExist]);

  // Let top-level ErrorBoundary see any async errors
  if (asyncError) throw asyncError;

  if (!isLoading && prProfiles.length === 0) return null;
  if (!company.page_config?.show_prs && !previewMode) return null;

  return (
    <SectionCard
      title="Our PR Representatives"
      editable={!previewMode && editable}
      onEdit={!previewMode ? () => setOpenEditModal("PR_PROFILES") : null}
      editButtonId={!previewMode ? "modal-button-PR_PROFILES" : undefined}
    >
      {isLoading ? (
        <SidebarSkeletonList numberOfLines={3} />
      ) : (
        <ul className="list-unstyled mb-0">
          {prProfiles.map((prProfile) => (
            <li className="ms-1 mb-2 animated faster fadeIn" key={prProfile.id}>
              <ListRow
                href={prProfileUrl({ slug: prProfile.slug, migrate_id: prProfile.migrate_id })}
                imageUrl={prProfile.profile_image_url}
                title={prProfile.name}
                subtitleTitle={prProfile.title}
                subtitleCompanyName={prProfile.promoter_company_name}
                subtitleCompanyHref={
                  prProfile.promoter_company_slug
                    ? companyUrl({
                        slug: prProfile.promoter_company_slug,
                        migrate_id: prProfile.promoter_company_migrate_id,
                      })
                    : null
                }
              />
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

export default PrProfileList;

PrProfileList.propTypes = {
  previewMode: PropTypes.bool,
};
